import { Button, Separator } from "@heroui/react";
import { Icon } from "@iconify/react";
import { useEffect, useState } from "react";
import { useRegisterSW } from "virtual:pwa-register/react";
import { ActionBar } from "@heroui-pro/react";
import { watchServiceWorkerUpdates } from "@/utils/service-worker";

// If the new worker never takes control (e.g. nothing was waiting), force a
// reload so the button can never appear stuck.
const RELOAD_FALLBACK_DELAY = 3000;

export default function PwaUpdatePrompt() {
  const [dismissed, setDismissed] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [registration, setRegistration] = useState<ServiceWorkerRegistration>();

  const {
    needRefresh: [needRefresh],
  } = useRegisterSW({
    onRegisteredSW(_swUrl, registration) {
      if (!registration) return;

      // If user dismissed the prompt last session, a SW is stuck in waiting -
      // activate it immediately so a single refresh gets the new version
      if (registration.waiting) {
        registration.waiting.postMessage({ type: "SKIP_WAITING" });
      }

      setRegistration(registration);
    },
  });

  const visible = needRefresh && !dismissed;

  useEffect(() => {
    if (!registration) return;

    return watchServiceWorkerUpdates(registration);
  }, [registration]);

  // Backstop: also reload if the controller changes (fires when the new worker
  // claims the page). Guarded so we only ever reload once.
  useEffect(() => {
    let refreshing = false;
    const onControllerChange = () => {
      if (refreshing) return;
      refreshing = true;
      window.location.reload();
    };

    navigator.serviceWorker?.addEventListener(
      "controllerchange",
      onControllerChange,
    );

    return () => {
      navigator.serviceWorker?.removeEventListener(
        "controllerchange",
        onControllerChange,
      );
    };
  }, []);

  const handleUpdate = async () => {
    setUpdating(true);

    let registration: ServiceWorkerRegistration | undefined;

    try {
      registration = await navigator.serviceWorker?.getRegistration();
    } catch {
      registration = undefined;
    }

    const waiting = registration?.waiting;

    if (waiting) {
      // Reload the moment the new worker finishes activating. This is more
      // reliable than `controllerchange` alone, which only fires if the new
      // worker claims existing clients - not guaranteed in prompt mode.
      waiting.addEventListener("statechange", () => {
        if (waiting.state === "activated") window.location.reload();
      });
      waiting.postMessage({ type: "SKIP_WAITING" });
    }

    // Safety net: if there's no waiting worker, or it never activates, reload
    // anyway so the click is never a silent no-op.
    window.setTimeout(() => {
      window.location.reload();
    }, RELOAD_FALLBACK_DELAY);
  };

  return (
    <ActionBar aria-label="App update" isOpen={visible}>
      <ActionBar.Prefix>
        <Icon
          className="text-accent shrink-0"
          height={20}
          icon="solar:refresh-bold"
          width={20}
        />
        <span className="text-sm text-foreground whitespace-nowrap">
          A new version is available
        </span>
      </ActionBar.Prefix>
      <Separator />
      <ActionBar.Content>
        <Button
          isDisabled={updating}
          size="sm"
          variant="tertiary"
          onPress={() => setDismissed(true)}
        >
          Later
        </Button>
        <Button
          isDisabled={updating}
          size="sm"
          variant="primary"
          onPress={handleUpdate}
        >
          {updating ? "Updating…" : "Update now"}
        </Button>
      </ActionBar.Content>
    </ActionBar>
  );
}
